// Tipos e helpers das páginas de subscrições do painel de administração da plataforma.

import { formatCurrency } from '../../shared/utils/currency'

// ── Módulos ─────────────────────────────────────────────────────────────────

export type Modulo =
    | 'FINANCAS'
    | 'CONTROLO_ACESSO'
    | 'AREAS_COMUNS'
    | 'ESTACIONAMENTO'
    | 'MANUTENCAO'
    | 'ARRENDAMENTO'
    | 'COMUNICACAO'
    | 'ASSEMBLEIAS'

export const MODULOS: Modulo[] = [
    'FINANCAS',
    'CONTROLO_ACESSO',
    'AREAS_COMUNS',
    'ESTACIONAMENTO',
    'MANUTENCAO',
    'ARRENDAMENTO',
    'COMUNICACAO',
    'ASSEMBLEIAS',
]

export const MODULO_LABEL: Record<Modulo, string> = {
    FINANCAS: 'Finanças',
    CONTROLO_ACESSO: 'Controlo de acesso',
    AREAS_COMUNS: 'Áreas comuns',
    ESTACIONAMENTO: 'Estacionamento',
    MANUTENCAO: 'Manutenção',
    ARRENDAMENTO: 'Arrendamento',
    COMUNICACAO: 'Comunicação',
    ASSEMBLEIAS: 'Assembleias',
}

// ── Planos ──────────────────────────────────────────────────────────────────

export interface Plano {
    id: string
    nome: string
    preco: number
    /** Máx. de condomínios do tenant. Irrelevante quando `propriedadesIlimitadas`. */
    totalPropriedades: number
    /** `true` → o plano não impõe limite de condomínios. */
    propriedadesIlimitadas: boolean
    /** Máx. de utilizadores do tenant. `0` → sem limite. */
    limiteUtilizadores: number
    modulosDisponiveis: Modulo[]
    activo: boolean
    /** `false` → plano demo/interno: as subscrições neste plano não geram cobranças. */
    cobravel: boolean
    emUso: boolean
    criadoEm: string
}

export interface GuardarPlanoBody {
    nome: string
    preco: number
    /** Omitir/`null` quando `propriedadesIlimitadas` for `true`. */
    totalPropriedades: number | null
    propriedadesIlimitadas: boolean
    /** Máx. de utilizadores do tenant. `0` → sem limite. */
    limiteUtilizadores: number
    modulosDisponiveis: Modulo[]
    activo: boolean
    /** `false` → plano demo, não faturado. */
    cobravel: boolean
}

// ── Subscrições ─────────────────────────────────────────────────────────────

export type CicloFaturacao = 'MENSAL' | 'ANUAL'
export type EstadoSubscricao = 'ACTIVA' | 'SUSPENSA' | 'CANCELADA'

export interface Subscricao {
    id: string
    tenantId: string
    tenantNome: string | null
    planoId: string
    planoNome: string | null
    estado: EstadoSubscricao
    cicloFaturacao: CicloFaturacao
    preco: number
    inicioEm: string
    fimEm: string | null
    proximaFaturacaoEm: string | null
    criadoEm: string
}

export const CICLO_LABEL: Record<CicloFaturacao, string> = {
    MENSAL: 'Mensal',
    ANUAL: 'Anual',
}

export const ESTADO_SUBSCRICAO_LABEL: Record<EstadoSubscricao, string> = {
    ACTIVA: 'Activa',
    SUSPENSA: 'Suspensa',
    CANCELADA: 'Cancelada',
}

export const ESTADO_SUBSCRICAO_TONE: Record<EstadoSubscricao, { bg: string; color: string }> = {
    ACTIVA: { bg: '#D1FAE5', color: '#065F46' },
    SUSPENSA: { bg: '#FEF3C7', color: '#B45309' },
    CANCELADA: { bg: '#F3F4F6', color: '#6B7280' },
}

// ── Cobranças ───────────────────────────────────────────────────────────────

export type EstadoCobranca = 'PENDENTE' | 'PAGA' | 'VENCIDA' | 'CANCELADA'
export type OrigemCobranca = 'AUTOMATICA' | 'MANUAL'

export interface Cobranca {
    id: string
    subscricaoId: string
    tenantId: string
    tenantNome: string | null
    referencia: string
    periodoInicio: string
    periodoFim: string
    valor: number
    valorPago: number
    emDivida: number
    emitidaEm: string
    vencimentoEm: string
    pagaEm: string | null
    estado: EstadoCobranca
    origem: OrigemCobranca
    criadoEm: string
}

export const ESTADO_COBRANCA_LABEL: Record<EstadoCobranca, string> = {
    PENDENTE: 'Pendente',
    PAGA: 'Paga',
    VENCIDA: 'Vencida',
    CANCELADA: 'Cancelada',
}

export const ESTADO_COBRANCA_TONE: Record<EstadoCobranca, { bg: string; color: string }> = {
    PENDENTE: { bg: '#DBEAFE', color: '#1D4ED8' },
    PAGA: { bg: '#D1FAE5', color: '#065F46' },
    VENCIDA: { bg: '#FEE2E2', color: '#B91C1C' },
    CANCELADA: { bg: '#F3F4F6', color: '#6B7280' },
}

export const ESTADOS_COBRANCA: EstadoCobranca[] = ['PENDENTE', 'PAGA', 'VENCIDA', 'CANCELADA']

// ── Pagamentos ──────────────────────────────────────────────────────────────

export type MetodoPagamento = 'TRANSFERENCIA' | 'NUMERARIO' | 'MULTICAIXA' | 'CARTAO' | 'OUTRO'

export interface Pagamento {
    id: string
    cobrancaId: string
    cobrancaReferencia: string | null
    tenantId: string
    tenantNome: string | null
    valor: number
    pagoEm: string
    metodo: MetodoPagamento
    referencia: string | null
    notas: string | null
    criadoEm: string
}

export const METODOS_PAGAMENTO: MetodoPagamento[] = [
    'TRANSFERENCIA',
    'NUMERARIO',
    'MULTICAIXA',
    'CARTAO',
    'OUTRO',
]

export const METODO_PAGAMENTO_LABEL: Record<MetodoPagamento, string> = {
    TRANSFERENCIA: 'Transferência',
    NUMERARIO: 'Numerário',
    MULTICAIXA: 'Multicaixa',
    CARTAO: 'Cartão',
    OUTRO: 'Outro',
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatMoney(valor: number | null | undefined): string {
    if (valor == null || Number.isNaN(valor)) return '—'
    return formatCurrency(valor, 'MZN')
}
